import { useCallback } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';
import {
  ProgressTracker,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
  ProgressTrackerLayout,
} from '@amzn/stencil-react-components/progress-tracker';

import { ReviewWizardStep, REVIEW_WIZARD_STEP_ORDER } from '../logic/reviewTypes';

export interface ReviewWizardStepperProps {
  /** Currently-active step. */
  readonly currentStep: ReviewWizardStep;
  /** Called when the user selects a step to jump to it. */
  readonly onStepSelect: (step: ReviewWizardStep) => void;
  /** Display label per step (translated by the caller). A Map (not a keyed
   *  object) so the union-typed step lookup doesn't trip
   *  security/detect-object-injection. */
  readonly labels: ReadonlyMap<ReviewWizardStep, string>;
}

/**
 * Per-step status from its position relative to the active step.
 *
 * Only the active step is `Current`; every other step (whether before or after
 * the cursor) is `Available`. We deliberately do NOT mark visited steps
 * `Complete`: `Complete` renders a checkmark, which claims the step's fields
 * are done — but merely navigating past a step doesn't validate it. Real
 * completion ticks are deferred to the publish-validation work, where a step
 * is only `Complete` once its fields actually pass validation.
 */
function resolveStepStatus(
  stepIndex: number,
  activeIndex: number,
): ProgressTrackerStepStatus {
  if (stepIndex === activeIndex) return ProgressTrackerStepStatus.Current;
  return ProgressTrackerStepStatus.Available;
}

/**
 * The 3-step progress indicator for the review wizard (Audit Criteria →
 * Sub Questions → Question Configuration).
 *
 * Thin wrapper over Stencil's `ProgressTracker`: maps our `ReviewWizardStep`
 * enum + active index onto the tracker's numeric `selectedStep` /
 * `onStepSelected`, and renders one `ProgressTrackerStep` per step with the
 * caller-supplied (translated) label. Presentational — the caller owns
 * `currentStep`.
 */
export function ReviewWizardStepper({
  currentStep,
  onStepSelect,
  labels,
}: ReviewWizardStepperProps): JSX.Element {
  const activeIndex = REVIEW_WIZARD_STEP_ORDER.indexOf(currentStep);

  // ProgressTracker reports selection as the step's numeric index; translate
  // back to the corresponding ReviewWizardStep for the caller.
  const handleStepSelected = useCallback(
    (stepIndex: number) => {
      // `.at()` (method call, not computed index) keeps this off the
      // security/detect-object-injection sink; stepIndex is a number from
      // ProgressTracker, never user input.
      const step = REVIEW_WIZARD_STEP_ORDER.at(stepIndex);
      if (step) onStepSelect(step);
    },
    [onStepSelect],
  );

  // Full-width wrapper so the ProgressTracker's `nav > ol` stretches to fill
  // the container and its `flex: 1` steps distribute evenly across the row.
  // Without this the tracker shrinks to its content width and bunches the
  // steps into the left third.
  return (
    <Col width="100%">
      <ProgressTracker
        selectedStep={activeIndex}
        onStepSelected={handleStepSelected}
        layout={ProgressTrackerLayout.Default}
        aria-label="Review wizard steps"
      >
        {REVIEW_WIZARD_STEP_ORDER.map((step, index) => (
          <ProgressTrackerStep
            key={step}
            labelText={labels.get(step) ?? ''}
            status={resolveStepStatus(index, activeIndex)}
          />
        ))}
      </ProgressTracker>
    </Col>
  );
}
